import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  // State management for tasks and UI
  const [tasks, setTasks] = useState([]); // Array of task objects
  const [newTask, setNewTask] = useState(''); // New task title input
  const [newDescription, setNewDescription] = useState(''); // New task description input
  const [searchTerm, setSearchTerm] = useState(''); // Search input value
  const [editingTask, setEditingTask] = useState(null); // Currently edited task ID
  const [editText, setEditText] = useState(''); // Edited task title
  const [editDescription, setEditDescription] = useState(''); // Edited task description

  // Load tasks from localStorage on initial render
  useEffect(() => {
    const savedTasks = JSON.parse(localStorage.getItem('tasks')) || [];
    setTasks(savedTasks);
  }, []);

  // Save tasks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  // Add a new task to the list
  const addTask = (e) => {
    e.preventDefault();
    if (newTask.trim()) {
      setTasks([...tasks, { 
        id: Date.now(), // Unique ID using timestamp
        text: newTask, 
        description: newDescription,
        completed: false 
      }]);
      // Reset input fields
      setNewTask('');
      setNewDescription('');
    }
  };

  // Toggle task completion status
  const toggleTask = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  // Delete a task from the list
  const deleteTask = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  // Start editing a task
  const startEditing = (task) => {
    setEditingTask(task.id);
    setEditText(task.text);
    setEditDescription(task.description || '');
  };

  // Save edited task changes
  const saveEdit = (id) => {
    setTasks(tasks.map(task =>
      task.id === id 
        ? { ...task, text: editText, description: editDescription }
        : task
    ));
    setEditingTask(null);
  };

  // Filter tasks based on search term
  const filteredTasks = tasks.filter(task => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return task.text.toLowerCase().includes(searchLower) || 
             (task.description && task.description.toLowerCase().includes(searchLower));
    }
    return true;
  });

  return (
    <div className="app">
      {/* Application header */}
      <h1>My To-Do List</h1>
      
      {/* Search functionality */}
      <div className="search-container">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search tasks..."
          className="search-input"
        />
      </div>

      {/* Task input form */}
      <form onSubmit={addTask} className="task-form">
        <div className="input-group">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Task title..."
            className="task-input"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Task description..."
            className="description-input"
          />
        </div>
        <button type="submit" className="add-button">Add</button>
      </form>

      {/* Task list */}
      <ul className="task-list">
        {filteredTasks.map(task => (
          <li key={task.id} className={`task-item ${task.completed ? 'completed' : ''}`}>
            <div className="task-content">
              {/* Edit mode or view mode */}
              {editingTask === task.id ? (
                <div className="edit-form">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="edit-input"
                  />
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="edit-description"
                    placeholder="Add description..."
                  />
                  <button onClick={() => saveEdit(task.id)} className="save-button">
                    Save
                  </button>
                </div>
              ) : (
                <>
                  <div className="task-header">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => toggleTask(task.id)}
                      className="task-checkbox"
                    />
                    <span className="task-text">{task.text}</span>
                  </div>
                  {task.description && (
                    <p className="task-description">{task.description}</p>
                  )}
                </>
              )}
            </div>
            {/* Task action buttons */}
            <div className="task-actions">
              {editingTask !== task.id && (
                <button
                  onClick={() => startEditing(task)}
                  className="edit-button"
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => deleteTask(task.id)}
                className="delete-button"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default App;



